/**
 * usePermissions Hook
 * Check user permissions and role-based access
 */

import { useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS, ROLE_PERMISSIONS, hasPermission, canManageRole } from '../constants/permissions';
import { isPlatformRole, isTenantRole, ROLE_HIERARCHY } from '../constants/roles';

export function usePermissions() {
  const { user } = useAuth();

  // Get all permissions for current user
  const userPermissions = useMemo(() => {
    if (!user?.role) return [];
    return ROLE_PERMISSIONS[user.role] || [];
  }, [user?.role]);

  // Check if user has a specific permission
  const can = useCallback((permission) => {
    if (!user?.role) return false;
    return hasPermission(user.role, permission);
  }, [user?.role]);

  // Check multiple permissions (all must be true)
  const canAll = useCallback((permissions) => {
    return permissions.every(p => can(p));
  }, [can]);

  // Check multiple permissions (any must be true)
  const canAny = useCallback((permissions) => {
    return permissions.some(p => can(p));
  }, [can]);

  // Check if user can manage another user based on roles
  const canManageUser = useCallback((targetRole) => {
    if (!user?.role) return false;
    return canManageRole(user.role, targetRole);
  }, [user?.role]);

  // Check if user is platform admin
  const isPlatformAdmin = useMemo(() => {
    return user?.role && isPlatformRole(user.role);
  }, [user?.role]);

  // Check if user is tenant admin or higher
  const isTenantAdmin = useMemo(() => {
    if (!user?.role) return false;
    return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY.tenant_admin;
  }, [user?.role]);

  // Check if user can access admin panel
  const canAccessAdmin = useMemo(() => {
    return can(PERMISSIONS.PLATFORM_VIEW) || can(PERMISSIONS.TENANTS_VIEW);
  }, [can]);

  // Check if user can manage billing
  const canManageBilling = useMemo(() => {
    return can(PERMISSIONS.BILLING_MANAGE);
  }, [can]);

  // Check if user can manage settings
  const canManageSettings = useMemo(() => {
    return can(PERMISSIONS.SETTINGS_EDIT);
  }, [can]);

  // Check if user can manage users
  const canManageUsers = useMemo(() => {
    return can(PERMISSIONS.USERS_CREATE) || can(PERMISSIONS.USERS_EDIT);
  }, [can]);

  // Check if user can edit pricing
  const canEditPricing = useMemo(() => {
    return can(PERMISSIONS.PRICING_EDIT);
  }, [can]);

  // Check if user can manage API keys (generate/revoke)
  const canManageApiKey = useMemo(() => {
    return can(PERMISSIONS.API_KEYS_MANAGE);
  }, [can]);

  // Check if user can manage webhooks
  const canManageWebhooks = useMemo(() => {
    return can(PERMISSIONS.WEBHOOKS_MANAGE);
  }, [can]);

  return {
    // Raw permissions
    permissions: userPermissions,
    
    // Permission checks
    can,
    canAll,
    canAny,
    canManageUser,
    
    // Role checks
    isPlatformAdmin,
    isTenantAdmin,
    
    // Common permission shortcuts
    canAccessAdmin,
    canManageBilling,
    canManageSettings,
    canManageUsers,
    canEditPricing,
    canManageApiKey,
    canManageWebhooks,

    // Constants
    PERMISSIONS,
  };
}

export default usePermissions;

